// Card #1639 — design §6.1's 7 mode types, as the kebab-case string values
// mode.modeType actually carries. Card #1648 (round-3 judge finding) found
// this file's own local copy of the map had been re-invented a second time
// in packages/score-common with SCREAMING_SNAKE values, a value-incompatible
// collision on the same wire field. score-common is now the single source of
// truth (packages/score-common/src/constants.js's MODE_TYPES, kept
// kebab-case because that's what's already pinned by shipped Cypress specs,
// e.g. apps/score-fe/cypress/e2e/scoreboard-screenshots.cy.js:57's literal
// `modeType: "mid-class-result"`) — this file re-exports it as `MODE_TYPE`
// (the name every existing call site here already imports) instead of
// keeping its own copy.
//
// Imports the `score-common/constants` SUBPATH, not the package root
// ("score-common"). The root export resolves to dist/score-common.js, which
// transitively bundles Node-only modules (node:os, node:process, node:tty)
// via the generated OpenAPI client — fine for score-api (Node), fatal for
// this file, which Cypress's webpack preprocessor browser-bundles for
// scoreboard.cy.js/scoreboard-qr.cy.js. That broke both specs at compile
// time (round-3 gate finding) until this file was pointed at the subpath
// instead. constants.js itself has zero imports, so the subpath is trivially
// browser-safe on its own, same pattern as this package's existing
// ./permissions / ./events / ./catalog subpaths.
import { MODE_TYPES } from "score-common/constants";

export const MODE_TYPE = MODE_TYPES;

// design §7.1 originally listed 4 sponsor-capable types: 1 (Class Summary
// [Detailed]), 2 (Class Summary [Simple]), 6 (Mid-Class Result), 7 (End of
// Class Result) — the Summary and Ranked-list families from §6.1's table.
// Card #1794 (2026-08-20 pare-down) removed types 1, 2, and 6 entirely, so
// END_OF_CLASS_RESULT is the sole survivor. Single-entry types (3, 4, 5) —
// 4 also removed by #1794 — never honored sponsorId. Ranked-list types get
// the wide (10:2) strip (§7.1: "wide 10:2 strip on list layouts, square on
// info blocks"); the square variant had no surviving caller left once the
// two Summary types left, so it is not carried forward as a dead map entry.
const SPONSOR_VARIANT_BY_MODE_TYPE = {
  [MODE_TYPE.END_OF_CLASS_RESULT]: "wide",
};

// Returns "square" | "wide" for a sponsor-capable modeType, or null for a
// type that never honors sponsorId (or an unrecognized/absent modeType —
// fails closed, never renders a strip for a type this map doesn't know).
//
// Card #1797 — MODE_TYPE.BLANK is deliberately absent from the map above,
// so this returns null for it via the same fails-closed `?? null`: no
// sponsor strip on the Blank view.
export function sponsorVariantForModeType(modeType) {
  return SPONSOR_VARIANT_BY_MODE_TYPE[modeType] ?? null;
}

// Card #1641 (design §6.2) originally excluded RIDE_INTRO_DETAILED (type 3,
// §6.2 item 3) from this set — a single ride's full intro detail (bio,
// owner, city/state) is shown for the whole time that ride is up, and was
// judged not to need a QR back to a results page mid-intro.
//
// Card #2283 OVERRULES that: docs/score/scoreboard-design.md §6.2 item 3 is
// now stale, superseded by two newer, higher authorities — p4.html itself
// (the approved, signed-off 2026-09-04 design of record for
// RIDE_INTRO_DETAILED, which renders the QR corner) and card #2283's own
// AC2 ("SCAN FOR RESULTS sits left of the QR in the footer banner", stated
// for this exact mode type). RIDE_INTRO_DETAILED is QR-capable as of this
// card; a comment was left on #2283 flagging §6.2 item 3 as stale (superseding
// tracked separately by #2272 — not reopened here).
//
// Card #1794 (2026-08-20 pare-down) removed types 1, 2, 4, and 6
// (CLASS_SUMMARY_DETAILED, CLASS_SUMMARY_SIMPLE, MID_RIDE_SIMPLE,
// MID_CLASS_RESULT) — RIDE_INTRO_DETAILED (3), POST_RIDE_SCORE (5), and
// END_OF_CLASS_RESULT (7) are the three QR-capable survivors.
const QR_CAPABLE_MODE_TYPES = new Set([
  MODE_TYPE.RIDE_INTRO_DETAILED, // 3 — card #2283, overrules stale §6.2 item 3
  MODE_TYPE.POST_RIDE_SCORE, // 5 — "...never 'final score'; QR"
  MODE_TYPE.END_OF_CLASS_RESULT, // 7 — "...banner...; QR"
]);

// Returns true for a modeType QRCorner should render on (design §6.2, as
// overruled by card #2283 for RIDE_INTRO_DETAILED — see the Set above), and
// false for any unrecognized/absent modeType.
//
// Card #1797 — MODE_TYPE.BLANK is deliberately absent from the Set above,
// so this returns false for it (no QR corner on the Blank view either).
export function isQrCapableModeType(modeType) {
  return QR_CAPABLE_MODE_TYPES.has(modeType);
}

// Card #1843 (policy settled on #2474): "tolerate-unknown, never 400" means
// a modeType this vocabulary no longer recognizes (a retired one, e.g.
// #1794's four removals) must still render as SOMETHING readable rather
// than a blank label — both in RemotePage's mode list and in
// ModeEditorDialog's greyed/disabled Select option for the current value.
// Turns "mid-ride-simple" into "Mid Ride Simple"; null/empty stays null so
// callers can fall back to their own default label.
export function humanizeModeType(modeType) {
  if (!modeType) return null;
  return modeType
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
